import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2, XCircle, Copy, Server, Loader2 } from "lucide-react";

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <div className="grid grid-cols-12 gap-3 items-center">
      <div className="col-span-4 overline">{label}</div>
      <div className="col-span-7 font-mono text-sm text-zinc-100 break-all">{value}</div>
      <button
        onClick={() => {
          navigator.clipboard.writeText(String(value));
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }}
        className="col-span-1 inline-flex justify-end text-zinc-400 hover:text-signal"
        data-testid={`copy-${label.toLowerCase()}`}
      >
        <Copy size={14} />
        {copied && <span className="ml-1 text-[10px] uppercase tracking-widest">OK</span>}
      </button>
    </div>
  );
}

export default function OrderSuccess() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, [id]);

  if (err) {
    return (
      <div className="container-pad py-24 font-mono text-zinc-400" data-testid="order-error">
        // {err}
      </div>
    );
  }
  if (!order) {
    return (
      <div className="container-pad py-24 flex items-center gap-2 text-zinc-500">
        <Loader2 size={16} className="animate-spin" /> <span className="font-mono text-xs uppercase tracking-widest">LOADING ORDER…</span>
      </div>
    );
  }

  const isPaid = order.status === "paid";
  const isFailed = order.status === "failed";

  return (
    <div data-testid="order-success-page" className="container-pad py-12 md:py-20 max-w-3xl">
      <div
        className={`card-flat p-8 ${
          isPaid ? "border-signal/60" : isFailed ? "border-red-900" : ""
        }`}
      >
        <div className="flex items-center gap-3">
          {isPaid && <CheckCircle2 size={22} className="text-signal" />}
          {isFailed && <XCircle size={22} className="text-red-400" />}
          {!isPaid && !isFailed && <Loader2 size={22} className="animate-spin text-zinc-400" />}
          <div className="overline">// ORDER {order.id.slice(0, 8).toUpperCase()}</div>
        </div>

        <h1 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">
          {isPaid ? (
            <>Payment confirmed. <span className="text-signal">Server provisioned.</span></>
          ) : isFailed ? (
            <>Payment <span className="text-red-400">failed</span>.</>
          ) : (
            <>Processing…</>
          )}
        </h1>

        <div className="mt-6 grid grid-cols-12 gap-3 font-mono text-sm">
          <div className="col-span-4 overline">PRODUCT</div>
          <div className="col-span-8 text-zinc-200">{order.service_name}</div>
          <div className="col-span-4 overline">PLAN</div>
          <div className="col-span-8 text-zinc-200">{order.plan_name}</div>
          <div className="col-span-4 overline">AMOUNT</div>
          <div className="col-span-8 text-zinc-200">${order.amount_usd.toFixed(2)} USD</div>
          <div className="col-span-4 overline">CARD</div>
          <div className="col-span-8 text-zinc-200">•••• •••• •••• {order.card_last4 || "—"}</div>
          <div className="col-span-4 overline">STATUS</div>
          <div className="col-span-8">
            <span
              className={`badge-mono ${
                isPaid ? "border-signal/60 text-signal" : isFailed ? "border-red-900 text-red-400" : ""
              }`}
            >
              {order.status?.toUpperCase()}
            </span>
          </div>
          {order.failure_reason && (
            <>
              <div className="col-span-4 overline">REASON</div>
              <div className="col-span-8 text-red-300">{order.failure_reason}</div>
            </>
          )}
        </div>

        {isPaid && order.delivery && (
          <div className="mt-8 border-t border-zinc-800 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Server size={16} className="text-signal" />
              <span className="font-mono text-xs tracking-widest text-white">
                SERVER CREDENTIALS
              </span>
            </div>

            <div className="space-y-3 card-flat p-5">
              <CopyField label="HOST" value={order.delivery.server_host} />
              <CopyField label="PORT" value={order.delivery.server_port} />
              <CopyField label="USERNAME" value={order.delivery.server_username} />
              <CopyField label="PASSWORD" value={order.delivery.server_password} />
              {order.delivery.docs_url && (
                <CopyField label="DOCS" value={order.delivery.docs_url} />
              )}
            </div>

            <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
              Store these securely. They will not be shown again in plain text.
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/dashboard" className="btn-signal h-11 px-5 text-sm font-mono tracking-widest" data-testid="go-to-dashboard">
            GO TO DASHBOARD
          </Link>
          <Link to="/catalog" className="btn-ghost h-11 px-5 text-sm font-mono tracking-widest">
            BROWSE MORE
          </Link>
        </div>
      </div>
    </div>
  );
}
